import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, TrendingUp, Gift, DollarSign, Download, Clock, UserPlus, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [data, setData] = useState(null);
  const [refs, setRefs] = useState(null);
  const [compEmail, setCompEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [compLink, setCompLink] = useState("");

  const loadMembers = () => api.get("/admin/members").then((r) => setData(r.data)).catch(() => setData({ members: [], total_members: 0, avg_modules_completed: 0, comp_members: 0 }));
  const loadRefs = () => api.get("/admin/referrals").then((r) => setRefs(r.data)).catch(() => setRefs({ payouts: [], total_referrals: 0, total_owed: 0, total_payable: 0, total_held: 0, total_paid_out: 0 }));

  useEffect(() => { loadMembers(); loadRefs(); }, []);

  const togglePaid = async (id, paid) => {
    try {
      await api.post(`/admin/referrals/${id}/pay`, { paid });
      loadRefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update payout");
    }
  };

  const createComp = async () => {
    if (!compEmail.trim()) return;
    setCreating(true);
    setCompLink("");
    try {
      const r = await api.post("/admin/comp-member", { email: compEmail.trim() });
      setCompLink(r.data.set_password_link);
      setCompEmail("");
      toast.success("Comp/beta member created. Set-password link ready + emailed.");
      loadMembers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create comp member");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(compLink); toast.success("Link copied"); };

  const exportCsv = () => {
    const rows = refs?.payouts || [];
    const header = ["Referrer", "Referred member", "Purchase date", "Amount owed (USD)", "Status", "On hold until", "Paid at"];
    const lines = rows.map((p) => [
      p.referrer_email || "",
      p.referred_email || "",
      p.purchase_date ? new Date(p.purchase_date).toISOString().slice(0, 10) : "",
      p.amount.toFixed(2),
      p.on_hold ? "on_hold" : p.status,
      p.on_hold ? new Date(p.eligible_at).toISOString().slice(0, 10) : "",
      p.paid_at ? new Date(p.paid_at).toISOString() : "",
    ]);
    const csv = [header, ...lines].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `referral-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stat = (Icon, value, label, testid) => (
    <div className="card-paper" style={{ padding: 22 }}>
      <Icon size={22} color="#2038E6" />
      <div className="font-archivo" data-testid={testid} style={{ fontWeight: 900, fontSize: 36 }}>{value}</div>
      <div className="eyebrow">{label}</div>
    </div>
  );

  const badge = (text, bg, color = "#0F1317", title = "") => (
    <span className="font-mono-plex" title={title} style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 6, background: bg, color }}>{text}</span>
  );

  return (
    <div data-testid="admin-page">
      <div className="eyebrow" style={{ color: "#2038E6" }}>Admin</div>
      <h1 className="h-display" style={{ fontSize: 40, marginTop: 6 }}>Members</h1>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, margin: "24px 0" }}>
        {stat(Users, data?.total_members ?? "...", "Paying members", "stat-members")}
        {stat(TrendingUp, data?.avg_modules_completed ?? "...", "Avg modules completed", "stat-avg")}
        {stat(UserPlus, data?.comp_members ?? "...", "Comp / beta (excluded)", "stat-comp")}
      </div>

      {/* ADD COMP / BETA MEMBER */}
      <div className="card-paper" style={{ padding: 20, marginBottom: 24 }} data-testid="comp-member-form">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <UserPlus size={18} color="#2038E6" />
          <h3 className="font-archivo" style={{ fontWeight: 800, fontSize: 16 }}>Add comp / beta member</h3>
        </div>
        <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>Creates a free full-access member (no payment). Excluded from revenue and member metrics. A set-password link is generated and emailed to them.</p>
        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <input
            type="email"
            value={compEmail}
            onChange={(e) => setCompEmail(e.target.value)}
            placeholder="member@email.com"
            data-testid="comp-email-input"
            style={{ flex: "1 1 240px", padding: "10px 14px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14 }}
          />
          <button className="btn-cobalt" onClick={createComp} disabled={creating} data-testid="comp-create-btn" style={{ padding: "10px 20px", fontSize: 13 }}>
            {creating ? "Creating..." : "Create comp member"}
          </button>
        </div>
        {compLink && (
          <div style={{ marginTop: 14, padding: 12, background: "#eef0eb", borderRadius: 10 }} data-testid="comp-link-box">
            <div className="eyebrow" style={{ marginBottom: 6 }}>Set-password link (also emailed) · valid 7 days</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ fontSize: 12, wordBreak: "break-all", flex: 1 }}>{compLink}</code>
              <button className="btn-outline" onClick={copyLink} style={{ padding: "6px 12px", fontSize: 12 }}><Copy size={13} /> Copy</button>
            </div>
          </div>
        )}
      </div>

      <div className="card-paper" style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="admin-members-table">
          <thead>
            <tr style={{ background: "#0F1317", color: "#E9ECE7" }}>
              {["Email", "Type", "Joined", "Modules", "Weekend steps"].map((h) => (
                <th key={h} className="font-mono-plex" style={{ textAlign: "left", padding: "12px 16px", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.members || []).map((m) => (
              <tr key={m.id} style={{ borderBottom: "1px solid var(--line)" }} data-testid="admin-member-row">
                <td style={{ padding: "12px 16px" }}>{m.email}</td>
                <td style={{ padding: "12px 16px" }}>{m.comp ? badge("Comp / Beta", "#DDE3FF") : badge("Paid", "#FFDF57")}</td>
                <td style={{ padding: "12px 16px", color: "var(--muted)" }}>{m.purchase_date ? new Date(m.purchase_date).toLocaleDateString() : "-"}</td>
                <td style={{ padding: "12px 16px" }}>{m.modules_completed} / {m.module_count}</td>
                <td style={{ padding: "12px 16px" }}>{m.weekend_steps_completed} / 5</td>
              </tr>
            ))}
            {data && data.members.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, color: "var(--muted)" }}>No members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* REFERRALS & PAYOUTS */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 48, flexWrap: "wrap", gap: 12 }}>
        <h2 className="h-display" style={{ fontSize: 32 }}>Referrals &amp; Payouts</h2>
        <button className="btn-outline" onClick={exportCsv} data-testid="referrals-export-csv" style={{ padding: "10px 16px", fontSize: 12 }}>
          <Download size={15} style={{ marginRight: 6, verticalAlign: "middle", display: "inline" }} /> Export CSV
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 16, margin: "20px 0" }}>
        {stat(Gift, refs?.total_referrals ?? "...", "Total referrals", "stat-referrals")}
        {stat(DollarSign, refs ? `$${refs.total_payable ?? 0}` : "...", "Payable now", "stat-payable")}
        {stat(Clock, refs ? `$${refs.total_held ?? 0}` : "...", `On hold (${31}-day window)`, "stat-held")}
        {stat(DollarSign, refs ? `$${refs.total_paid_out}` : "...", "Paid out", "stat-paidout")}
      </div>

      <div className="card-paper" style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="admin-referrals-table">
          <thead>
            <tr style={{ background: "#0F1317", color: "#E9ECE7" }}>
              {["Referrer", "Referred member", "Purchase date", "Amount", "Status", "Action"].map((h) => (
                <th key={h} className="font-mono-plex" style={{ textAlign: "left", padding: "12px 16px", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(refs?.payouts || []).map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid var(--line)" }} data-testid="admin-referral-row">
                <td style={{ padding: "12px 16px" }}>{p.referrer_email || "-"}</td>
                <td style={{ padding: "12px 16px" }}>{p.referred_email}</td>
                <td style={{ padding: "12px 16px", color: "var(--muted)" }}>{p.purchase_date ? new Date(p.purchase_date).toLocaleDateString() : "-"}</td>
                <td style={{ padding: "12px 16px" }} className="font-mono-plex">${p.amount.toFixed(2)}</td>
                <td style={{ padding: "12px 16px" }}>
                  {p.status === "paid"
                    ? badge("paid", "#FFDF57", "#0F1317", p.paid_at ? `Paid ${new Date(p.paid_at).toLocaleString()}` : "")
                    : p.on_hold
                      ? badge(`on hold → ${new Date(p.eligible_at).toLocaleDateString()}`, "#F3D6A6", "#0F1317", `Releases after the ${31}-day guarantee window`)
                      : badge("payable", "#CFEBD3")}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <button
                    onClick={() => togglePaid(p.id, p.status !== "paid")}
                    data-testid={`referral-toggle-${p.id}`}
                    className="btn-outline"
                    disabled={p.on_hold}
                    title={p.on_hold ? `On hold until ${new Date(p.eligible_at).toLocaleDateString()} (guarantee window)` : ""}
                    style={{ padding: "6px 12px", fontSize: 11, opacity: p.on_hold ? 0.45 : 1, cursor: p.on_hold ? "not-allowed" : "pointer" }}
                  >
                    {p.status === "paid" ? "Mark unpaid" : "Mark as paid"}
                  </button>
                </td>
              </tr>
            ))}
            {refs && refs.payouts.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, color: "var(--muted)" }}>No referrals yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
